import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Auth0ErrorHandler: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const { logout, isLoading, loginWithRedirect } = useAuth0();
  const [error, setError] = useState<{
    type: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    const errorType = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorType) {
      console.warn('Auth0 error detected:', {
        error: errorType,
        description: errorDescription,
      });

      setError({
        type: errorType,
        description: errorDescription || 'Authentication failed',
      });

      setTimeout(() => {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }, 100);
    }
  }, [searchParams]);

  const handleRetryLogin = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleTryAgain = () => {
    loginWithRedirect();
  };

  if (error && !isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4'>
        <div className='w-full max-w-md animate-fade-in rounded-lg border border-red-200 bg-white p-8 shadow-lg'>
          {/* Error Icon */}
          <div className='mb-4 flex items-center justify-center'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-100 ring-4 ring-red-50'>
              <svg
                className='h-8 w-8 text-red-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>
          </div>

          {/* Error Title */}
          <h2 className='mb-3 text-center text-2xl font-semibold text-gray-900'>
            Access Not Available
          </h2>

          {/* Beta Badge */}
          <div className='mb-4 flex items-center justify-center'>
            <span className='rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800'>
              Private Beta
            </span>
          </div>

          {/* Error Message */}
          <p className='mb-6 text-center text-sm leading-relaxed text-gray-600'>
            We are currently in beta and only accepting invited users. If you
            believe you should have access, please contact us at{' '}
            <a
              href='mailto:help@acconto.helpscoutapp.com'
              className='font-medium text-blue-600 hover:text-blue-700 hover:underline'
            >
              help@acconto.helpscoutapp.com
            </a>
            .
          </p>

          {/* Error Details Box */}
          <div className='mb-6 rounded-md bg-red-50 p-4'>
            <p className='text-xs text-red-800'>
              <span className='font-semibold'>Error Code:</span> {error.type}
            </p>
            {error.description && error.type !== error.description && (
              <p className='mt-1 text-xs text-red-700'>{error.description}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col gap-3'>
            <button
              onClick={handleTryAgain}
              className='flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            >
              <svg
                className='h-4 w-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                />
              </svg>
              Try Again
            </button>

            <button
              onClick={handleRetryLogin}
              className='rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
            >
              Return to Login
            </button>
          </div>

          {/* Help Text */}
          <div className='mt-6 border-t border-gray-200 pt-6'>
            <p className='text-center text-xs text-gray-500'>
              Need access?{' '}
              <a
                href='https://docs.google.com/forms/d/12BLzxerAs2k1H2sn2AaVypkAbqWmPLcAfGM2kcOr4rQ'
                className='font-medium text-blue-600 hover:text-blue-700 hover:underline'
              >
                Request an invitation
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default Auth0ErrorHandler;
